// The PM's view of an area (SCHEMA.md "Before UAT" §5): per surface, the PM's
// assessed hours and a note. The assessment is blind (#627): no measured hours,
// variance or comparison here, so the PM can't anchor on the measure; the
// comparison is only at the desk, in REVIEW's Hours priced.
import { approvedHoursOf, areaAssessedHours, surfaceLabel, updateAssessment, type SurfacesDoc, type SurfaceType } from '@higgins/m2q-pricing';
import { useDeviceQuery } from '../lib/hooks';
import { loadSurfaceTypes } from '../lib/reference';
import { NumberField } from './number-field';
import { SavedTextField } from './saved-text-field';
import { sourceLabel } from './hours-priced-text';
import { formatHours, storedHours } from './surface-format';

interface AssessPanelProps {
  doc: SurfacesDoc;
  /** The area's columns: which hours the review chose to price it (approved-hours.ts). */
  areaFields: Readonly<Record<string, unknown>>;
  /** Washed and not painted (#510): the surfaces' hours aren't priced. */
  washOnly: boolean;
  /** hig_assessnotes. */
  notes: string;
  /** Every change, with the area's new assessed hours (null when nothing is assessed). */
  onChange: (doc: SurfacesDoc, assessedHours: number | null) => void;
  onNotes: (text: string) => void;
}

/** Which hours price the area: the review chooses at the desk, so the PM's hours never change the price by themselves. */
function PricedFrom({ fields, doc, types }: { fields: Readonly<Record<string, unknown>>; doc: SurfacesDoc; types: ReadonlyMap<string, SurfaceType> }) {
  const { source, chosen, hours } = approvedHoursOf(fields, doc, types);
  return (
    <p className="rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-900">
      The review at the desk decides which hours price the job. This area is priced from: <b>{chosen && source !== 'Measured' ? `${sourceLabel(source)} ${formatHours(hours)}` : 'Measured'}</b>.
    </p>
  );
}

export function AssessPanel({ doc, areaFields, washOnly, notes, onChange, onNotes }: AssessPanelProps) {
  const types = useDeviceQuery(loadSurfaceTypes, []);
  const byId = types?.byId ?? new Map<string, SurfaceType>();
  const groupName = new Map(doc.groups.map((g) => [g.id, `${g.name} × ${g.count}`]));
  const apply = (next: SurfacesDoc) => onChange(next, areaAssessedHours(next));
  const assessedTotal = areaAssessedHours(doc);

  return (
    <div className="space-y-3">
      {washOnly && <p className="rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-900">Wash only — priced from the wash m². Hours assessed here are painting hours and aren&rsquo;t priced.</p>}
      {!washOnly && doc.surfaces.length > 0 && <PricedFrom fields={areaFields} doc={doc} types={byId} />}
      {doc.surfaces.length === 0 && <p className="text-sm text-slate-600">Nothing measured in this area yet.</p>}
      {doc.surfaces.map((s) => (
        <div key={s.id} className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium text-slate-900">
              {surfaceLabel(s)}
              {s.groupId && groupName.has(s.groupId) && <span className="ml-2 text-xs font-normal text-slate-500">{groupName.get(s.groupId)}</span>}
            </span>
            <label className="flex items-center gap-2 text-sm">
              <span className="whitespace-nowrap text-slate-600">Assessed h</span>
              <NumberField label={`Assessed hours, ${surfaceLabel(s)}`} value={s.assessedHours ?? null} onChange={(v) => apply(updateAssessment(doc, s.id, { assessedHours: v ?? undefined }))} />
            </label>
          </div>
          <SavedTextField
            value={s.assessNote ?? ''}
            onSave={(note) => apply(updateAssessment(doc, s.id, { assessNote: note || undefined }))}
            placeholder="Note (optional)"
            label={`Note, ${surfaceLabel(s)}`}
            className="mt-2 bg-white text-sm"
          />
        </div>
      ))}
      <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
        Assessed <span className="font-medium tabular-nums">{assessedTotal === null ? '—' : formatHours(storedHours(assessedTotal))}</span>
      </div>
      <div>
        <label htmlFor="assess-notes" className="text-sm font-medium text-slate-800">
          PM assessment notes
        </label>
        <SavedTextField id="assess-notes" label="PM assessment notes" value={notes} onSave={onNotes} placeholder="Overall observations, access concerns, condition…" className="mt-1" rows={3} />
      </div>
    </div>
  );
}
